/**
 * Spacing System
 * Defines spacing scale and conversion utilities
 */

import { BASE_FONT_SIZE } from './typography';

/**
 * Convert pixels to rem with px fallback
 * @param px - Pixel value
 * @returns CSS string with rem and px fallback
 * @example pxToRem(16) => 'clamp(16px, 1rem, 1rem)'
 */
export function pxToRem(px: number): string {
  const rem = px / BASE_FONT_SIZE;
  return `${rem}rem`;
}

/**
 * Get both px and rem values for fallback purposes
 * @param px - Pixel value
 * @returns Object with both px and rem values
 */
function getPxAndRem(px: number): { px: string; rem: string } {
  return {
    px: `${px}px`,
    rem: pxToRem(px),
  };
}

/**
 * Spacing scale based on 4px grid
 */
const spacing = {
  xs: pxToRem(4),
  sm: pxToRem(8),
  md: pxToRem(16),
  lg: pxToRem(24),
  xl: pxToRem(32),
  xxl: pxToRem(48),
  xxxl: pxToRem(64),
} as const;

export type SpacingKey = keyof typeof spacing;

/**
 * Get spacing value by key
 */
function getSpacing(key: SpacingKey): string {
  return spacing[key];
}
